import {
  Children,
  Fragment,
  Suspense,
  isValidElement,
  lazy,
  useEffect,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type RefObject,
} from 'react'
import { CatchBoundary, Link, useNavigate, useRouter, useRouterState } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowUp, Square, Trash2, X } from 'lucide-react'
import Markdown, { type Components, type ExtraProps } from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { useAuth } from '@/lib/auth'
import { downloadBlob } from '@/lib/download-blob'
import { cn } from '@/lib/utils'
import { Button } from '@/ui/button'
import { LoadingNotice } from '@/ui/loading-notice'
import { ResizeHandle } from '@/ui/resize-handle'
import { useAnnounce } from '@/shared/providers/announcer-provider'
import {
  assistantContext,
  type AssistantContextItem,
  type PageAssistantContext,
} from '@/lib/assistant-context.generated'
import { useSelectedEntityId } from '@/lib/use-selected-entity'

import { parseChartSpec } from './assistant-chart-spec'
import { AssistantTierPicker } from './assistant-tier-picker'
import { messageClassName, messageLabel, outcomeText } from './assistant-copy'
import { useAssistantSession } from './use-assistant-session'

const AssistantChart = lazy(() => import('./assistant-chart').then((m) => ({ default: m.AssistantChart })))

export const ASSISTANT_WIDTH = { min: 320, max: 960, initial: 384 }

function markdownComponents(streaming: boolean): Components {
  return {
    a: ({ href, children }) => {
      return href && visibleText(children)
        ? <a href={href}>{children}</a>
        : <>{children}</>
    },
    table: ({ children }) => (
      <div className="max-w-full overflow-x-auto">
        <table>{children}</table>
      </div>
    ),
    pre: ({ node, children }) => {
      const source = chartSource(node)
      if (source == null) return <pre>{children}</pre>
      const spec = parseChartSpec(source)
      if (spec) {
        return (
          <CatchBoundary getResetKey={() => source} errorComponent={() => <pre>{children}</pre>}>
            <Suspense fallback={<LoadingNotice message="Loading chart..." />}>
              <AssistantChart spec={spec} />
            </Suspense>
          </CatchBoundary>
        )
      }
      return streaming
        ? <p role="status" className="text-muted-foreground">Preparing chart...</p>
        : <pre>{children}</pre>
    },
  }
}

function chartSource(node: ExtraProps['node']): string | null {
  const code = node?.children[0]
  if (code?.type !== 'element' || code.tagName !== 'code') return null
  const languages = code.properties.className
  if (!Array.isArray(languages) || !languages.includes('language-chart')) return null
  return code.children.map((child) => (child.type === 'text' ? child.value : '')).join('')
}

function visibleText(children: ReactNode): string {
  return Children.toArray(children)
    .map((child) => {
      if (typeof child === 'string' || typeof child === 'number') return String(child)
      if (isValidElement<{ children?: ReactNode }>(child)) return visibleText(child.props.children)
      return ''
    })
    .join('')
    .replace(/\p{Default_Ignorable_Code_Point}/gu, '')
    .trim()
}

function routeAssistantContext(
  match: (PageAssistantContext & { context: { assistantContext?: AssistantContextItem[] } }) | undefined,
): AssistantContextItem[] {
  return match
    ? [
        assistantContext.page({ routeId: match.routeId, params: match.params }),
        ...(match.context.assistantContext ?? []),
      ]
    : []
}

function AssistantMarkdown({ text, streaming }: { text: string; streaming: boolean }) {
  return (
    <div className="[&>*+*]:mt-2 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h4]:font-semibold [&_h5]:font-semibold [&_h6]:font-semibold [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_a]:underline [&_a]:underline-offset-2 [&_a]:focus-visible:outline-2 [&_a]:focus-visible:outline-offset-2 [&_code]:rounded [&_code]:bg-background [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-background [&_pre]:p-2 [&_pre_code]:break-normal [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_table]:border-collapse [&_table]:text-xs [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_hr]:border-border">
      <Markdown
        skipHtml
        disallowedElements={['img', 'input']}
        remarkPlugins={[remarkGfm]}
        components={markdownComponents(streaming)}
      >
        {text}
      </Markdown>
    </div>
  )
}

export function AssistantPanel({
  open,
  setOpen,
  width,
  setWidth,
  toggleRef,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  width: number
  setWidth: (width: number) => void
  toggleRef: RefObject<HTMLButtonElement | null>
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const router = useRouter()
  const { user } = useAuth()
  const announce = useAnnounce()
  const match = useRouterState({ select: (state) => state.matches.at(-1) })
  const navigateTo = async (path: string) => {
    const previous = router.state.location.pathname
    try {
      await navigate({ to: path as '/' })
    } catch {
      return false
    }
    return previous !== path && router.state.location.pathname === path
  }
  const context = routeAssistantContext(match)
  useEffect(() => {
    if (import.meta.env.MODE === 'development') {
      console.info('[assistant] context changed', context)
    }
  }, [context])
  const selectedEntityId = useSelectedEntityId()
  const {
    input,
    setInput,
    messages,
    transcript,
    busy,
    stopping,
    resolving,
    error,
    inputRef,
    scrollRef,
    close,
    send,
    stop,
    resolve,
    startNew,
    tierOptions,
    pickTier,
  } = useAssistantSession({
    open,
    setOpen,
    toggleRef,
    boundIdentity: `${user?.userId ?? ''}:${user?.impersonation?.actorUserId ?? ''}`,
    queryClient,
    navigate: navigateTo,
    announce,
    download,
    context,
    selectedEntityId,
  })
  const stopRef = useRef<HTMLButtonElement>(null)
  useLayoutEffect(() => {
    if (busy) stopRef.current?.focus()
  }, [busy])

  return (
    <section
      role="dialog"
      aria-label="AI assistant"
      aria-hidden={open ? undefined : true}
      inert={!open}
      className={cn(
        'bg-sidebar fixed right-0 bottom-0 z-50 flex w-[var(--assistant-width)] max-w-full pr-2 pb-2 transition-[translate,visibility] duration-300 ease-drawer',
        user?.impersonation ? 'top-18' : 'top-10',
        open ? 'visible translate-x-0' : 'invisible translate-x-full',
      )}
    >
      <ResizeHandle
        label="Resize assistant"
        orientation="vertical"
        value={width}
        min={ASSISTANT_WIDTH.min}
        max={ASSISTANT_WIDTH.max}
        direction={-1}
        className="-left-1"
        onChange={setWidth}
        onDragChange={(dragging) => document.documentElement.classList.toggle('assistant-resizing', dragging)}
      />
      <div
        data-assistant-surface
        className="bg-background flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl dark:border-[3px] dark:border-sidebar-border"
      >
        <header className="border-border flex h-10 shrink-0 items-center justify-between border-b px-3">
          <h2 className="text-sm font-semibold">Assistant</h2>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Clear conversation"
              disabled={busy}
              onClick={() => void startNew()}
            >
              <Trash2 className="size-4" />
            </Button>
            <Button type="button" variant="ghost" size="icon" aria-label="Close assistant" onClick={close}>
              <X className="size-4" />
            </Button>
          </div>
        </header>
        <div ref={scrollRef} className="min-w-0 flex-1 space-y-3 overflow-x-hidden overflow-y-auto p-3" aria-live="polite">
          {messages.length === 0 && <p className="text-muted-foreground text-sm">How can I help?</p>}
          {transcript.map((entry) => {
            if (entry.kind === 'message') {
              const { message, index } = entry
              return message.role === 'assistant' ? (
                <div key={index} className={messageClassName(message.role)}>
                  <span className="sr-only">{messageLabel(message.role)}</span>
                  {message.destination ? <>
                    <p>{message.text}</p>
                    <Link to={message.destination.path as '/'} className="underline underline-offset-2">{message.destination.label}</Link>
                  </> : <AssistantMarkdown text={message.text} streaming={busy && index === messages.length - 1} />}
                </div>
              ) : (
                <p key={index} className={messageClassName(message.role)}>
                  <span className="sr-only">{messageLabel(message.role)}</span>
                  {message.text}
                </p>
              )
            }
            const { id, details, status } = entry.proposal
            return (
              <div key={id} className="border-border rounded-md border p-2 text-sm">
                <ProposalSummary id={`assistant-proposal-${id}`} details={details} />
                {status === 'pending' ? (
                  <div className="flex gap-2">
                    <Button type="button" size="sm" aria-describedby={`assistant-proposal-${id}`} disabled={resolving != null} onClick={() => void resolve(id, true)}>{resolving === id ? 'Approving...' : 'Approve'}</Button>
                    <Button type="button" size="sm" variant="outline" aria-describedby={`assistant-proposal-${id}`} disabled={resolving != null} onClick={() => void resolve(id, false)}>Reject</Button>
                  </div>
                ) : (
                  <p className="text-muted-foreground">{outcomeText[status]}</p>
                )}
              </div>
            )
          })}
          {(busy || stopping) && <p role="status" className="text-muted-foreground text-sm">{stopping ? 'Stopping...' : 'Thinking...'}</p>}
          {error && <p role="alert" className="text-destructive text-sm">{error}</p>}
        </div>
        <form
          className="border-border border-t p-3"
          onSubmit={(event) => {
            event.preventDefault()
            void send()
          }}
        >
          <div className="border-input bg-input/20 focus-within:border-ring focus-within:ring-ring/30 flex flex-col rounded-xl border focus-within:ring-2">
            <label className="sr-only" htmlFor="assistant-message">Message</label>
            <textarea
              id="assistant-message"
              ref={inputRef}
              value={input}
              maxLength={4000}
              rows={2}
              disabled={busy || stopping}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  void send()
                }
              }}
              placeholder="Ask the assistant"
              className="placeholder:text-muted-foreground min-h-14 w-full resize-none bg-transparent px-3 pt-2.5 text-sm outline-none"
            />
            <div className="flex items-center justify-between gap-2 px-2 pb-2">
              {tierOptions && (
                <AssistantTierPicker
                  enabledTiers={tierOptions.enabledTiers}
                  tier={tierOptions.tier}
                  onTierChange={pickTier}
                  disabled={busy || stopping}
                />
              )}
              {busy || stopping ? (
                <Button key="stop" type="button" size="icon" className="ml-auto rounded-full" aria-label="Stop response" ref={stopRef} disabled={stopping} focusableWhenDisabled onClick={stop} onKeyDown={(event) => {
                  if (event.repeat) event.preventDefault()
                }}>
                  <Square className="size-4" />
                </Button>
              ) : (
                <Button key="send" type="submit" size="icon" className="ml-auto rounded-full" aria-label="Send message" disabled={!input.trim()}>
                  <ArrowUp className="size-4" />
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </section>
  )
}

function ProposalSummary({ id, details }: { id: string; details: unknown }) {
  const fields = proposalFields(details)
  if (typeof fields === 'string') {
    return <p id={id} className="mb-2 break-words">{fields}</p>
  }
  return (
    <dl id={id} className="mb-2 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 break-words">
      {fields.map(({ label, value }) => (
        <Fragment key={label}>
          <dt className="text-muted-foreground">{label}</dt>
          <dd>{value}</dd>
        </Fragment>
      ))}
    </dl>
  )
}

function proposalFields(details: unknown): string | { label: string; value: string }[] {
  if (typeof details === 'string') return details
  if (details === null || typeof details !== 'object') return String(details ?? '')
  const fields = Object.entries(details as Record<string, unknown>)
    .filter(([, value]) => !isEmpty(value))
    .map(([key, value]) => ({ label: fieldLabel(key), value: fieldValue(value) }))
  return fields.length > 0 ? fields : 'No details to show.'
}

function isEmpty(value: unknown) {
  return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)
}

function fieldLabel(key: string) {
  const spaced = key.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

function fieldValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(fieldValue).join('; ')
  if (value !== null && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, nested]) => !isEmpty(nested))
      .map(([key, nested]) => `${fieldLabel(key)}: ${fieldValue(nested)}`)
      .join(', ')
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

function download(name: string, contentType: string, content: string) {
  const bytes = Uint8Array.from(atob(content), (character) => character.charCodeAt(0))
  downloadBlob(new Blob([bytes], { type: contentType }), name)
}
